import type { App } from '../../app.js';
import { h, button, toast } from '../dom.js';
import { Action, DEFAULT_BINDINGS, GraphicsPreset, defaultSettings } from '../../persist.js';
import { keyLabel, PAD_BUTTON_LABELS } from '../../input.js';

const ACTION_LABELS: Record<Action, string> = {
  throttle: 'Throttle',
  brake: 'Brake',
  steerLeft: 'Steer left',
  steerRight: 'Steer right',
  drift: 'Drift (hold + steer)',
  boost: 'Boost',
  respawn: 'Reset to track',
  pitchUp: 'Pitch up (airborne)',
  pitchDown: 'Pitch down (airborne)',
  lookBack: 'Look back / spectate next',
  pause: 'Pause',
};

export function buildSettingsScreen(app: App): HTMLElement {
  let tab: 'graphics' | 'audio' | 'controls' | 'gameplay' = 'graphics';
  const body = h('div', { class: 'col', style: 'gap: 14px; min-height: 380px;' });

  const row = (label: string, control: HTMLElement, hint = '') =>
    h(
      'div',
      { class: 'row', style: 'justify-content:space-between; gap: 24px; align-items:center;' },
      h(
        'div',
        { style: 'max-width: 30ch;' },
        h('div', { class: 'subtle', style: 'font-size:13px; color: var(--text);', text: label }),
        hint ? h('div', { class: 'subtle', style: 'font-size:11px; text-transform:none;', text: hint }) : null
      ),
      control
    );

  const slider = (get: () => number, set: (v: number) => void, min = 0, max = 1) => {
    const input = h('input', {
      type: 'range',
      min: String(min * 100),
      max: String(max * 100),
      value: String(Math.round(get() * 100)),
      style: 'width: 190px;',
    }) as HTMLInputElement;
    input.addEventListener('input', () => {
      set(Number(input.value) / 100);
      app.saveAll();
    });
    return input;
  };

  const toggle = (get: () => boolean, set: (v: boolean) => void) => {
    const b = button(get() ? 'ON' : 'OFF', () => {
      set(!get());
      b.textContent = get() ? 'ON' : 'OFF';
      b.classList.toggle('selected', get());
      app.saveAll();
      app.audio.play('ui_move');
    }, `small focusable${get() ? ' selected' : ''}`);
    return b;
  };

  const choices = <T extends string>(opts: T[], get: () => T, set: (v: T) => void, labels?: Record<string, string>) =>
    h(
      'div',
      { class: 'row wrap' },
      ...opts.map((o) =>
        button(
          (labels?.[o] ?? o).toUpperCase(),
          () => {
            set(o);
            app.saveAll();
            app.audio.play('ui_move');
            render();
          },
          `small focusable${get() === o ? ' selected' : ''}`
        )
      )
    );

  const render = () => {
    body.innerHTML = '';
    const s = app.settings;
    if (tab === 'graphics') {
      body.append(
        row(
          'Quality preset',
          choices<GraphicsPreset>(['performance', 'balanced', 'quality'], () => s.graphicsPreset, (v) => {
            s.graphicsPreset = v;
            app.renderer.applyQuality();
          }),
          'Performance disables bloom and halves resolution scale headroom'
        ),
        row('Render scale', slider(() => s.renderScale, (v) => {
          s.renderScale = Math.max(0.5, v);
          app.renderer.applyQuality();
        }, 0.5, 1), 'Lower for weak GPUs'),
        row('Field of view', slider(() => (s.baseFov - 55) / 40, (v) => (s.baseFov = 55 + v * 40)), '55–95°'),
        row('Effect intensity', slider(() => s.effectIntensity, (v) => (s.effectIntensity = v)), 'Particles, sparks, streaks'),
        row('Camera shake', slider(() => s.cameraShake, (v) => (s.cameraShake = v))),
        row('Reduced motion', toggle(() => s.reducedMotion, (v) => {
          s.reducedMotion = v;
          document.body.classList.toggle('reduced-motion', v);
        }), 'Softens FOV kicks, shake and animations'),
        row('Colorblind-friendly track edges', toggle(() => s.colorAssist, (v) => (s.colorAssist = v)), 'White/red edge language instead of hue pairs'),
        row('Fullscreen', button('TOGGLE', () => {
          if (document.fullscreenElement) document.exitFullscreen();
          else document.documentElement.requestFullscreen().catch(() => toast('Fullscreen blocked by browser', true));
        }, 'small focusable'))
      );
    } else if (tab === 'audio') {
      body.append(
        row('Master volume', slider(() => s.masterVolume, (v) => { s.masterVolume = v; app.audio.applyVolumes(); })),
        row('Music', slider(() => s.musicVolume, (v) => { s.musicVolume = v; app.audio.applyVolumes(); })),
        row('Effects', slider(() => s.sfxVolume, (v) => { s.sfxVolume = v; app.audio.applyVolumes(); })),
        row('Engine & ambience', slider(() => s.ambientVolume, (v) => { s.ambientVolume = v; app.audio.applyVolumes(); }))
      );
    } else if (tab === 'controls') {
      body.append(
        row('Steering sensitivity', slider(() => s.steerSensitivity / 1.5, (v) => (s.steerSensitivity = Math.max(0.3, v * 1.5)), 0.2, 1)),
        row('Invert pitch', toggle(() => s.invertPitch, (v) => (s.invertPitch = v))),
        h('div', { class: 'h2', text: 'BINDINGS — CLICK TO REBIND', style: 'margin-top: 6px;' })
      );
      for (const action of Object.keys(ACTION_LABELS) as Action[]) {
        const b = s.bindings[action];
        const keyBtn = button(b.keys.map(keyLabel).join(' / ') || '—', () => {
          keyBtn.textContent = 'PRESS A KEY…';
          app.input.captureNext(action, 'key', () => {
            app.saveAll();
            render();
          });
        }, 'small focusable mono');
        const padBtn = button(
          b.padButtons.map((i) => PAD_BUTTON_LABELS[i] ?? `BTN${i}`).join(' / ') || '—',
          () => {
            padBtn.textContent = 'PRESS A BUTTON…';
            app.input.captureNext(action, 'pad', () => {
              app.saveAll();
              render();
            });
          },
          'small focusable mono'
        );
        body.append(
          h(
            'div',
            { class: 'row', style: 'justify-content:space-between; gap:12px;' },
            h('span', { class: 'subtle', style: 'font-size:12px; flex:1;', text: ACTION_LABELS[action] }),
            keyBtn,
            padBtn
          )
        );
      }
      body.append(
        h('div', { class: 'row', style: 'margin-top: 10px;' },
          button('RESET BINDINGS', () => {
            s.bindings = structuredClone(DEFAULT_BINDINGS);
            app.saveAll();
            render();
            toast('Bindings reset to defaults.');
          }, 'small danger focusable'))
      );
    } else {
      body.append(
        row('Steering assist', toggle(() => s.assists.steeringHelp, (v) => (s.assists.steeringHelp = v)), 'Gentle centering when coasting'),
        row('Brake assist', toggle(() => s.assists.brakeHelp, (v) => (s.assists.brakeHelp = v)), 'Auto-airbrake into hard corners'),
        row('Race your ghost', toggle(() => s.showGhost, (v) => (s.showGhost = v)), 'Time trial & seeded tracks'),
        row('Speed units', choices(['kmh', 'vl'], () => s.speedUnits, (v) => (s.speedUnits = v as any), { kmh: 'KM/H', vl: 'VL' })),
        row(
          'Multiplayer server',
          (() => {
            const input = h('input', {
              type: 'text',
              placeholder: 'same origin (default)',
              value: s.serverUrl,
              style: 'width: 240px; font-size: 12px;',
            }) as HTMLInputElement;
            input.addEventListener('change', () => {
              s.serverUrl = input.value.trim();
              app.conn?.close();
              app.conn = null;
              app.saveAll();
            });
            return input;
          })(),
          'ws://host:port/ws — leave empty for default'
        ),
        row('Player name', (() => {
          const input = h('input', {
            type: 'text',
            maxlength: '20',
            value: s.playerName,
            style: 'width: 200px;',
          }) as HTMLInputElement;
          input.addEventListener('change', () => {
            s.playerName = input.value.trim().replace(/[^\w\-. ]/g, '').slice(0, 20);
            app.saveAll();
          });
          return input;
        })()),
        row('Reset all settings', button('RESET', () => {
          const name = s.playerName;
          Object.assign(s, defaultSettings());
          s.playerName = name;
          s.firstRunDone = true;
          app.saveAll();
          app.renderer.applyQuality();
          render();
          toast('Settings reset.');
        }, 'small danger focusable'))
      );
    }
  };

  const tabs = h(
    'div',
    { class: 'row', style: 'gap: 8px; margin-bottom: 18px;' },
    ...(['graphics', 'audio', 'controls', 'gameplay'] as const).map((t) =>
      button(
        t.toUpperCase(),
        () => {
          tab = t;
          app.audio.play('ui_move');
          renderTabs();
          render();
        },
        `small focusable${tab === t ? ' selected' : ''}`
      )
    )
  );
  const renderTabs = () => {
    [...tabs.children].forEach((c, i) => {
      c.classList.toggle('selected', (['graphics', 'audio', 'controls', 'gameplay'] as const)[i] === tab);
    });
  };

  render();

  return h(
    'div',
    { class: 'screen screen-dim' },
    h(
      'div',
      { class: 'panel', style: 'width: min(680px, 94vw); max-height: 88vh; overflow-y: auto;' },
      h(
        'div',
        { class: 'row', style: 'justify-content: space-between; margin-bottom: 14px;' },
        h('div', { class: 'h1', text: 'SETTINGS' }),
        button('◄ BACK', () => {
          app.audio.play('ui_back');
          app.show('menu');
        }, 'small focusable')
      ),
      tabs,
      body
    )
  );
}
